import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BehaviorSubject, catchError, combineLatest, EMPTY, map, Observable, tap } from 'rxjs';
import { SessionQuery } from '../../core/state';
import { Cmg1003LoanStore, Cmg1003ApplicationInfoQuery, Cmg1003BorrowerQuery, SecurityProfileQuery, Cmg1003LoanQuery } from '../../loan-state';
import { SecurityProfileModel } from '../../loan-state/security-profile';
import { Status } from '../models/ductech.enum';
import { Borrower, LoanApplicationModel, Prompt, SaveDisclosuresRequest, Section } from '../models/loan-application.model';
import { SectionStatusModel } from '../models/section-status.model';
import * as SupplementalUtil from '../models/utils/supplemental.util';
import { BorrowersQuery } from '../state/borrowers/borrowers.query';
import { BorrowersStore } from '../state/borrowers/borrowers.store';
import { DocutechGlobalQuery } from '../state/docutechGlobal/docutech-global.query';
import { DocutechGlobalStore } from '../state/docutechGlobal/docutech-global.store';
import { LoanApplicationQuery } from '../state/loan-application/loan-application.query';
import { LoanApplicationStore } from '../state/loan-application/loan-application.store';
import { SectionStatusQuery } from '../state/section-status/section-status.query';
import { SupplementalSectionPromptsQuery } from '../state/supplemental-section-prompts/supplemental-section-prompts.query';
import { SupplementalSectionPromptsStore } from '../state/supplemental-section-prompts/supplemental-section-prompts.store';
import { DisclosureDates } from '../models/disclosure-dates.model';
import { DisclosureDatesRepository } from '../state/disclosure-dates/disclosure-dates.repository';
import { APP_ENV } from 'apps/cmg-clear2-ui/src/environments/environment.model';
import { LoanStatus } from '../../cmg-1003/models/cmg-1003-common.enum';
import { REQUIRES_SECURITY_PROFILE } from '../../interceptors';
import { GetCCWPayloadModel } from '../../closing-cost-worksheet-15/models/get-ccw-payload.model';
import { AdditionalNotesVM, ClosingCostWorksheet, LePendingResponse } from '../../closing-cost-worksheet-15/models/ccw.models';
import { FailedToLoadModalComponent } from '../../core/components';
import { BorrowerModel, loanPurposeTypes, maritalStatusTypes } from '../../cmg-1003/models';
import { CmgUiUtilities } from '../../cmg-1003/components/common/cmg-ui-utils';
import { LoanPurposeType } from '../models/loan-information.model';
import { occupancyTypes } from '../models/lookups';
import { OccupancyType } from '../../shared/enums/occupancy-type.enum';
import { DisclosureApplication } from '../models/disclosure-type.model';

@Injectable({ providedIn: 'root' })
export class LoanApplicationService {
  private readonly _httpClient = inject(HttpClient);
  private readonly _loanApplicationQuery = inject(LoanApplicationQuery);
  private readonly _loanApplicationStore = inject(LoanApplicationStore);
  private readonly _borrowersStore = inject(BorrowersStore);
  private readonly _borrowersQuery = inject(BorrowersQuery);
  private readonly _1003BorrowersQuery = inject(Cmg1003BorrowerQuery);
  private readonly _docutechGlobalStore = inject(DocutechGlobalStore);
  private readonly _supplementalSectionPromptsStore = inject(SupplementalSectionPromptsStore);
  private readonly _supplementalSectionPromptsQuery = inject(SupplementalSectionPromptsQuery);
  private readonly _sectionStatusQuery = inject(SectionStatusQuery);
  private readonly _docutechGlobalQuery = inject(DocutechGlobalQuery);
  private readonly _securityProfileQuery = inject(SecurityProfileQuery);
  private readonly _sessionQuery = inject(SessionQuery);
  private readonly _disclosureDatesRepository = inject(DisclosureDatesRepository);
  private readonly _dialogService = inject(DialogService);
  private readonly _loanStore = inject(Cmg1003LoanStore);
  private readonly _loanQuery = inject(Cmg1003LoanQuery);
  private readonly _1003ApplicationQuery = inject(Cmg1003ApplicationInfoQuery);
  private readonly _environment = inject(APP_ENV);

  dialog: DynamicDialogRef;

  private _isDirty = false;
  savedLoan$ = new BehaviorSubject<LoanApplicationModel>(null);
  sectionStatus$ = this._sectionStatusQuery.select();
  isBorrowersFormDirty$ = this.isBorrowersFormDirty();
  isSupplementalSectionsFormDirty$ = this.isSupplementalSectionsFormDirty();
  isLoanAppFormDirty$ = this.isLoanApplicationFormDirty();
  isNeedsReview$ = this._sectionStatusQuery.select().pipe(map((t) => !!Object.keys(t)?.find((key) => t[key] === Status.NEEDS_REVIEW)));
  isRevalidateSelfDisclosure$ = new BehaviorSubject(false);
  isRevalidateRequestLE$ = new BehaviorSubject(false);

  canSaveLoanApplication$ = combineLatest([
    this.isBorrowersFormDirty$,
    this.isSupplementalSectionsFormDirty$,
    this.isLoanAppFormDirty$,
    this._docutechGlobalQuery.select('isDisclosuresResolution'),
  ]).pipe(
    map(
      ([isBorrowersFormDirty, isSupplementalSectionsFormDirty, isLoanAppFormDirty, isDisclosuresResolution]) =>
        !isDisclosuresResolution && (isBorrowersFormDirty || isSupplementalSectionsFormDirty || isLoanAppFormDirty),
    ),
    tap((canSave) => (this._isDirty = canSave)),
  );

  isDirty$ = this.canSaveLoanApplication$;

  isDirtyWithoutSupplemental$ = combineLatest([
    this.isBorrowersFormDirty$,
    this.isLoanAppFormDirty$,
    this._docutechGlobalQuery.select('isDisclosuresResolution'),
  ]).pipe(
    map(
      ([isBorrowersFormDirty, isLoanAppFormDirty, isDisclosuresResolution]) =>
        !isDisclosuresResolution && (isBorrowersFormDirty || isLoanAppFormDirty),
    ),
    tap((canSave) => (this._isDirty = canSave)),
  );

  canSecurityProfileOrderDisclosures$ = this._securityProfileQuery
    .selectActive((s) => s as SecurityProfileModel)
    .pipe(
      map(({ securityProfileId }) => {
        const securityProfileHasPermission = Object.values(this._environment.securityProfileId).find((s) => s === securityProfileId);
        return !!securityProfileHasPermission;
      }),
    );

  canPreviewDisclosures$ = combineLatest([this.isNeedsReview$, this._docutechGlobalQuery.select('isDisclosuresResolution')]).pipe(
    map(([isNeedsReview, isDisclosuresResolution]) => !isDisclosuresResolution && !isNeedsReview),
    tap((canPreviewDisclosures) => {
      if (!canPreviewDisclosures) {
        this._docutechGlobalStore.update({ canOrderDisclosure: false });
      }
    }),
  );

  canOrderDisclosures$ = combineLatest([
    this._docutechGlobalQuery.select('canOrderDisclosure'),
    this._docutechGlobalQuery.select('isDisclosuresResolution'),
    this.canSecurityProfileOrderDisclosures$,
    this._sessionQuery.sessionStatus$,
  ]).pipe(
    map(([canOrderDisclosure, isDisclosuresResolution, canSecurityProfileOrderDisclosures, loanStatus]) => {
      const statusCanOrder = [LoanStatus.LePending].find((s) => s === loanStatus);
      return canOrderDisclosure && !isDisclosuresResolution && canSecurityProfileOrderDisclosures && !!statusCanOrder;
    }),
  );

  canDeferToDisclosureDesk$ = combineLatest([
    this._docutechGlobalQuery.select('isDisclosuresResolution'),
    this._sessionQuery.sessionStatus$,
    this._disclosureDatesRepository.dates$,
  ]).pipe(
    map(([isDisclosuresResolution, loanStatus, dates]) => {
      return this.canDeferToDisclosureDesk(isDisclosuresResolution, loanStatus, dates);
    }),
  );

  canDeferToDisclosureDesk(isDisclosuresResolution: boolean, loanStatus: LoanStatus, dates?: DisclosureDates): boolean {
    const statusCanDefer = [LoanStatus.LePending, LoanStatus.CreditOnly, LoanStatus.PreApproved].find((s) => s === loanStatus);
    return !isDisclosuresResolution && !!statusCanDefer && !dates?.initialDisclosuresRequested;
  }

  isDirty() {
    return this._isDirty;
  }

  getLoanApplication(fileId: number) {
    const url = `${this._environment.retail.apiUrl}/disclosures/${fileId}`;
    return this._httpClient.get<LoanApplicationModel>(url).pipe(
      catchError(() => {
        return EMPTY;
      }),
      tap((loanApplication: LoanApplicationModel) => {
        this.updateLoanApplicationStore(loanApplication);
        this.savedLoan$.next({ ...loanApplication });

        if (loanApplication?.orderedDateTime || loanApplication?.deferredForInitialDisclosure) {
          this._docutechGlobalStore.update({
            canOrderDisclosure: false,
            canPreviewDisclosure: false,
            isDisclosuresResolution: true,
          });
        } else {
          this._docutechGlobalStore.update({
            canOrderDisclosure: false,
            isDisclosuresResolution: false,
          });
        }

        setTimeout(() => {
          this.setRollbackMark();
        }, 500);
      }),
    );
  }

  getUpdatedSupplementalSection(loanApplication: LoanApplicationModel, updatedPrompts: Prompt[]): Section[] {
    const sections = loanApplication?.supplemental?.sections;
    const updatedSections: Section[] = [];
    if (sections && sections.length > 0) {
      sections.forEach((section) => {
        const prompts: Prompt[] = [];

        section.prompts.forEach((current) => {
          updatedPrompts.forEach((updated) => {
            if (current.promptCode === updated.promptCode) {
              // Adds null to dropdowns
              // if (current.promptType === 'Dropdown' && current.promptSelectionOptions.findIndex(o => o == null) < 0) {
              //   current.promptSelectionOptions.push("")
              // }
              prompts.push({ ...current, promptValue: SupplementalUtil.getSaveValue(updated) });
            }
          });
        });

        updatedSections.push({ ...section, prompts });
      });
    }
    return updatedSections;
  }

  isBorrowersFormDirty() {
    return this._borrowersQuery.dirtyChecker.isDirty$;
  }

  isComplete(sectionStatus: SectionStatusModel): boolean {
    for (const statusKey in sectionStatus) {
      if (Object.prototype.hasOwnProperty.call(sectionStatus, statusKey)) {
        const status = sectionStatus[statusKey];
        if (status === Status.NEEDS_REVIEW || status === Status.NOTHING) {
          return false;
        }
      }
    }
    return true;
  }

  isLoanApplicationFormDirty() {
    return this._loanApplicationQuery.dirtyCheck.isDirty$;
  }

  isSupplementalSectionsFormDirty() {
    return this._supplementalSectionPromptsQuery.dirtyChecker.isDirty$;
  }

  reset() {
    this._loanApplicationQuery.dirtyCheck.reset();
    this._borrowersQuery.dirtyChecker.reset();
    this._supplementalSectionPromptsQuery.dirtyChecker.reset();
  }

  refreshDisclosureState() {
    const loanApplication = { ...this._loanApplicationQuery.getValue() };
    const borrowers: Borrower[] = this._borrowersQuery.getAll();
    const supplementalPrompts: Prompt[] = this._supplementalSectionPromptsQuery.getAll();
    const url = `${this._environment.retail.apiUrl}/disclosures/${loanApplication.fileDataId}`;

    if (!loanApplication?.fileDataId) {
      return;
    }

    this.updateBorrowersList(loanApplication, borrowers);
    if (loanApplication?.supplemental?.sections) {
      const supplemental = { ...loanApplication.supplemental };
      supplemental.sections = this.getUpdatedSupplementalSection(loanApplication, supplementalPrompts);
      loanApplication.supplemental = supplemental;
    }

    return this._httpClient.get(url).pipe(
      catchError(() => {
        this._docutechGlobalStore.update({
          canPreviewDisclosure: false,
        });
        return EMPTY;
      }),
      tap((response: LoanApplicationModel) => {
        this.updateLoanApplicationStore(response);
        this.savedLoan$.next({ ...response });
      }),
    );
  }

  saveDisclosure(payload: SaveDisclosuresRequest) {
    const url = `${this._environment.retail.apiUrl}/disclosures`;
    return this._httpClient.post(url, payload, { context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true) }).pipe(
      catchError(() => {
        return EMPTY;
      }),
    );
  }

  setRollbackMark() {
    this._loanApplicationQuery.dirtyCheck.setHead();
    this._borrowersQuery.dirtyChecker.setHead();
    this._supplementalSectionPromptsQuery.dirtyChecker.setHead();
    this._loanQuery.dirtyChecker2.dirtyChecker.setHead();
  }

  setTaxPeriodYear(oldTaxPeriodDateStr: string, newTaxPeriodYear: string) {
    if (newTaxPeriodYear && newTaxPeriodYear.length > 0 && oldTaxPeriodDateStr) {
      const oldTaxPeriodArray: string[] = oldTaxPeriodDateStr.split('/');
      oldTaxPeriodArray[2] = newTaxPeriodYear;
      return oldTaxPeriodArray.join('/');
    } else if (newTaxPeriodYear && newTaxPeriodYear.length > 0 && oldTaxPeriodDateStr === null) {
      const newDate: Date = new Date();
      const month = newDate.getMonth().toString();
      const day = newDate.getDay().toString();
      return month.toString().concat('/').concat(day).concat('/').concat(newTaxPeriodYear);
    }
    return oldTaxPeriodDateStr;
  }

  updateBorrowersList(loanApplication: LoanApplicationModel, updatedBorrowers: Borrower[]): void {
    const borrowerList = [...loanApplication.borrowers];
    for (let i = 0; i < borrowerList.length; i = i + 1) {
      const oldBorrower = { ...borrowerList[i] };
      for (const newBorrower of updatedBorrowers) {
        if (oldBorrower.borrowerID === newBorrower.borrowerID) {
          oldBorrower.taxPeriodYear1 = newBorrower?.taxPeriodYear1;
          oldBorrower.taxPeriodYear2 = newBorrower?.taxPeriodYear2;
          oldBorrower.filingStatus = newBorrower.filingStatus;
          oldBorrower.email = newBorrower.email;
          oldBorrower.irsForm4506ID = newBorrower.irsForm4506ID;
          oldBorrower.taxForm = newBorrower.taxForm;
        }
      }
      borrowerList[i] = oldBorrower;
    }

    loanApplication.borrowers = borrowerList;
  }

  updateLoanApplicationStore(loanApplication: LoanApplicationModel) {
    const { borrowers, supplemental } = loanApplication;
    this._loanApplicationStore.update(loanApplication);
    const borrowersClone = borrowers?.map((a) => Object.assign({}, a));
    const supplementalSectionsClone = supplemental?.sections?.map((a) => Object.assign({}, SupplementalUtil.parseSectionPrompts(a)));
    this._borrowersStore.set(borrowersClone);
    this._loanStore.update({ ...{ cA885PaymentScenario: loanApplication.cA885PaymentScenario } });
    const prompts: Prompt[] = [];

    supplementalSectionsClone.forEach((section) => {
      section.prompts.forEach((prompt) => prompts.push(prompt));
    });

    this._supplementalSectionPromptsStore.set(prompts);
    this.setRollbackMark();
  }

  getDisclosureDate(fileId: number): Observable<DisclosureDates> {
    const url = `${this._environment.retail.apiUrl}/disclosures/${fileId}/disclosure-dates`;
    return this._httpClient.get<DisclosureDates>(url).pipe(
      catchError(() => EMPTY),
      tap((disclosureDate) => {
        this._disclosureDatesRepository.stateChange(disclosureDate);
      }),
    );
  }

  getClosingCostWorksheet(payload: GetCCWPayloadModel) {
    const url = `${this._environment.retail.apiUrl}/ClosingCostWorksheet1dot5/GetClosingCostWorksheet`;
    return this._httpClient.post<ClosingCostWorksheet>(url, payload).pipe(catchError(() => EMPTY));
  }

  getAdditionalNotes(fileDataId: number): Observable<string> {
    const url = `${this._environment.retail.apiUrl}/ClosingCostWorksheet1dot5/GetAdditionalNotes/${fileDataId}`;
    return this._httpClient.get(url, { responseType: 'text' });
  }

  addNewNote(payload: AdditionalNotesVM): Observable<LePendingResponse> {
    const url = `${this._environment.retail.apiUrl}/ClosingCostWorksheet1dot5/PostNotes`;
    return this._httpClient.post<LePendingResponse>(url, payload);
  }

  displayError(error): void {
    const correlationId = error?.headers?.get('X-Plinth-Reqtraceid') ? `Correlation ID: ${error.headers?.get('X-Plinth-Reqtraceid')}` : '';
    const correlationDiv = correlationId ? `<div class="correlation mt-3 mb-3">${correlationId}</div>` : '<br/>';
    const header = 'Disclosures Error';

    this._dialogService.open(FailedToLoadModalComponent, {
      header: header,
      width: '512px',
      data: { message: correlationDiv },
      closable: false,
      styleClass: 'dialog-content-overflow-hidden',
    });
  }

  isDisplayTexasSpouseFields() {
    const activeApplicationId = this._1003ApplicationQuery.getActiveId();
    const borrowerIds = this.getAllBorrowerIdsFromCurrentApplications(+activeApplicationId);
    const borrowers: BorrowerModel[] = this._1003BorrowersQuery.getAll({
      filterBy: (b) => borrowerIds.some((bId) => bId === b.borrowerId),
    });
    const { loanPurpose, occupancyType } = this._loanApplicationQuery.getValue();

    // MaritalStatus: Married
    const marriedStatus = maritalStatusTypes[1];
    const isAnyBorrowerMarried = !!borrowers.find((b) => b.maritalStatus === marriedStatus.id);

    // transaction type valdation: Refinance
    const loanPurposeLookup = loanPurposeTypes.find((lpt) => lpt.value.toLowerCase() === loanPurpose.toLowerCase());
    const isLoanPurposeRefinance = LoanPurposeType.refinance === loanPurposeLookup.id;

    // Occupancy Type: Primary Residence
    const occupancyTypeLookup = occupancyTypes.find((occType) => occType.value.toLowerCase() === occupancyType.toLowerCase());
    const isOccupancyTypePrimaryResidence = OccupancyType.PrimaryResidence === occupancyTypeLookup.id;

    return isAnyBorrowerMarried && isLoanPurposeRefinance && isOccupancyTypePrimaryResidence;
  }

  getAllBorrowerIdsFromCurrentApplications(applicationId: number): number[] {
    const activeApp = this._1003ApplicationQuery.getEntity(applicationId);
    const borrowerIds = this._1003ApplicationQuery.getAll().map((application) => application.borrowerId);
    if (
      !CmgUiUtilities.isIdTemporary(activeApp.coBorrowerId) ||
      (CmgUiUtilities.isIdTemporary(activeApp.coBorrowerId) && activeApp.additionalBorrowerChecked)
    ) {
      borrowerIds.push(activeApp.coBorrowerId);
    }

    return borrowerIds;
  }

  updateDisclosureApplication(application: DisclosureApplication) {
    let { disclosureApplications } = this._loanApplicationQuery.getValue();
    disclosureApplications = disclosureApplications.map((app) => {
      if (application.applicationID === app.applicationID) {
        app = {
          ...application,
        };
      }
      return app;
    });

    this._loanApplicationStore.update({ ...{ disclosureApplications } });
  }

  setRevalidateStatusDisclosureRequestLE(revalidate: boolean) {
    this.isRevalidateRequestLE$.next(revalidate);
  }

  setRevalidateStatusSelfDisclosure(revalidate: boolean) {
    this.isRevalidateSelfDisclosure$.next(revalidate);
  }
}
